import { useState } from 'react';

import classNames from 'classnames';
import { KidsRatioResponse } from 'generated';

import { Box, Collapse, IconButton, styled } from '@mui/material';

import StatusBlock from './StatusBlock';
import { Icon } from '@sdb/planning-ui';

type RowProps = {
  times: string[];
  name: string;
  ratios: Record<number, KidsRatioResponse[]>;
  isWorkweek?: boolean;
};

const ROWS = [
  '0 jarigen',
  '1 jarigen',
  '2 jarigen',
  '3 jarigen',
  '4 - 7 jarigen',
  '7 - 13 jarigen'
];

const distributions = [
  'numberOfAge0',
  'numberOfAge1',
  'numberOfAge2',
  'numberOfAge3',
  'numberOfAge4To7',
  'numberOfAge7OrAbove'
];
const TableCell = styled('td')`
  vertical-align: top;
  padding: 4px 8px;
`;

export default function KidsTableRow({
  times,
  name,
  ratios,
  isWorkweek
}: RowProps) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const weekLength = isWorkweek ? 5 : 7;

  return (
    <>
      <tr>
        <TableCell>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box fontSize="12px">{name}</Box>
            <IconButton
              size="small"
              onClick={() => setExpanded((prev) => !prev)}
            >
              <Icon type={expanded ? 'chevron-up' : 'chevron-down'} />
            </IconButton>
          </Box>
        </TableCell>

        {Array.from({ length: weekLength }).map((_, index) => (
          <td key={`td__${index}`}>
            <Box
              display="flex"
              sx={{
                overflow: 'hidden',
                borderRadius: '4px'
              }}
            >
              {times.map((time) => (
                <StatusBlock
                  key={time}
                  expanded={expanded}
                  ratio={ratios[index + 1]?.find((v) => v.time === time)}
                />
              ))}
            </Box>
          </td>
        ))}
      </tr>

      {ROWS.map((row, rowIndex) => {
        return (
          <tr key={row}>
            <TableCell>
              <Collapse in={expanded}>{row}</Collapse>
            </TableCell>
            {Array.from({ length: weekLength }).map((_, index) => (
              <td key={index}>
                <Collapse in={expanded}>
                  <Box display="flex" bgcolor="lightGray.main" py={0.5}>
                    {times.map((time) => (
                      <Box
                        key={time}
                        flex="1"
                        display="flex"
                        justifyContent="center"
                      >
                        {(ratios[index + 1]?.find((v) => v.time === time)
                          ?.kidDistribution ?? {})[distributions[rowIndex]] ??
                          0}
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </td>
            ))}
          </tr>
        );
      })}
      <tr>
        <TableCell>
          <Collapse in={expanded}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>Aantal kinderen</Box>
            </Box>
          </Collapse>
        </TableCell>
        {Array.from({ length: weekLength }).map((_, index) => (
          <td key={index} className={classNames(expanded && 'cell')}>
            <Collapse in={expanded}>
              <Box display="flex">
                {times.map((time) => (
                  <Box key={time} flex="1" textAlign="center">
                    {ratios[index + 1]?.find((v) => v.time === time)
                      ?.kidDistribution?.total ?? 0}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </td>
        ))}
      </tr>
      <tr>
        <TableCell>
          <Collapse in={expanded}>Benodigde beroepskrachten</Collapse>
        </TableCell>
        {Array.from({ length: weekLength }).map((_, index) => (
          <td
            key={index}
            className={classNames(expanded && 'cell cell--total')}
          >
            <Collapse in={expanded}>
              <Box display="flex">
                {times.map((time) => (
                  <Box key={time} flex="1" textAlign="center">
                    {ratios[index + 1]?.find((v) => v.time === time)
                      ?.numberOfRequiredEmployees ?? '-'}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </td>
        ))}
      </tr>
    </>
  );
}
